import type { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

import { DRAWER_TRANSITION_DURATION_MS } from './drawer.constants';
import { DrawerConfirmation } from './DrawerConfirmation';
import { DrawerHeader } from './DrawerHeader';
import { LayerContext } from './LayerContext';
import type { StoredLayer } from './layerStoreReducer';
import { LayerStatus } from './types/LayerStatus';

const slideIn = keyframes`
    from { transform: translate3d(100%, 0, 0); }
    to { transform: translate3d(0, 0, 0); }
`;
const slideOut = keyframes`
    from { transform: translate3d(0, 0, 0); }
    to { transform: translate3d(calc(100% + ${theme.space[16]}), 0, 0); }
`;

const StyledLayer = styled(Box)<{ $width?: string; $index: number; $offset?: number; $isClosing?: boolean }>`
    position: fixed;
    top: ${theme.space[16]};
    bottom: ${theme.space[16]};
    right: ${theme.space[16]};
    border-radius: ${theme.radii[8]};
    background-color: ${theme.colors.background.default};
    box-shadow: ${theme.shadows.extraLarge};
    overflow: hidden;
    transition: transform ${DRAWER_TRANSITION_DURATION_MS}ms ease-in-out;
    animation: ${({ $isClosing }) => ($isClosing ? slideOut : slideIn)} ${DRAWER_TRANSITION_DURATION_MS}ms ease-in-out;
    animation-fill-mode: ${({ $isClosing }) => ($isClosing ? 'forwards' : 'none')};
    width: ${({ $width = '33vw' }) => $width};
    min-width: 400px;
    max-width: 800px;
    z-index: ${({ $index }) => $index + 1};
    transform: translateX(${({ $offset = 0 }) => -$offset}px);
`;

const LayerBackdrop = styled(Box)<{ $isCovered: boolean }>`
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: ${theme.colors.background.dimming};
    opacity: ${({ $isCovered }) => ($isCovered ? 1 : 0)};
    transition: opacity ${DRAWER_TRANSITION_DURATION_MS}ms ease-in-out;
    pointer-events: none;
`;

interface LayerProps {
    layer: StoredLayer;
    index: number;
    isTopLayer: boolean;
    isCovered: boolean;
    offset?: number;
    children: ReactNode;
}

export const Layer = ({ layer, index, isTopLayer, isCovered, offset, children }: LayerProps) => {
    const isClosing = layer.status === LayerStatus.CLOSING;
    const blocked = !isTopLayer || isClosing;

    return (
        <LayerContext.Provider value={{ id: layer.id, isTopLayer }}>
            <StyledLayer $width={layer.width} $index={index} $offset={offset} $isClosing={isClosing} inert={blocked}>
                <Box
                    display="grid"
                    gridTemplateRows="auto minmax(0, 1fr)"
                    height="100%"
                    tabIndex={-1}
                    role="region"
                    aria-labelledby={`drawer-title-${layer.id}`}
                >
                    <DrawerHeader layer={layer} disabled={blocked} />
                    <Box position="relative" minHeight={0} style={{ isolation: 'isolate' }}>
                        <Box height="100%" minHeight={0} overflow="auto" inert={blocked || !!layer.confirmation}>
                            {children}
                        </Box>
                        {layer.status === LayerStatus.CONFIRMING ? (
                            <DrawerConfirmation layerId={layer.id} confirmation={layer.confirmation} />
                        ) : null}
                    </Box>
                </Box>
                <LayerBackdrop data-testid="drawer-layer-backdrop" $isCovered={isCovered} />
            </StyledLayer>
        </LayerContext.Provider>
    );
};
